/**
 * AI Confirmation Service
 *
 * Manages user confirmations for destructive AI operations.
 * Listens to SSE 'confirmation-required' events via ConfirmationEventBus.
 *
 * ARCHITECTURE:
 * - EventSourceService receives SSE event
 * - EventSourceService emits via ConfirmationEventBus (singleton)
 * - AIConfirmationService subscribes to bus (per-workspace instance)
 * - React hook subscribes to AIConfirmationService
 * - Chat input renders the single active decision card
 */
import { Service } from '@toeverything/infra';

import { defaultWorkspaceApiClient } from '../../cloud';
import {
  confirmationEventBus,
  type ConfirmationEventDetail,
} from '../../cloud/services/eventsource';
import { WorkspaceService } from '../../workspace';

export interface ConfirmationRequest {
  id: string;
  toolName: string;
  description: string;
  metadata?: Record<string, unknown>;
}

export interface ConfirmationResponse {
  approved: boolean;
  reason?: string;
}

// sl-canvas2-8.31: Response from /respond endpoint includes tool result
export interface ConfirmationApiResponse {
  success: boolean;
  approved: boolean;
  toolResult?: {
    success: boolean;
    data?: unknown;
    error?: string;
  };
  reason?: string;
}

export class AIConfirmationService extends Service {
  private readonly handlers = new Set<(request: ConfirmationRequest) => void>();
  private unsubscribeFromBus: (() => void) | null = null;

  constructor(private readonly workspaceService: WorkspaceService) {
    super();
    // Subscribe to the event bus
    this.unsubscribeFromBus = confirmationEventBus.subscribe(
      (detail: ConfirmationEventDetail) => {
        this.notifyHandlers(detail);
      }
    );
  }

  override dispose(): void {
    // Clean up subscription when service is disposed
    if (this.unsubscribeFromBus) {
      this.unsubscribeFromBus();
      this.unsubscribeFromBus = null;
    }
    this.handlers.clear();
    super.dispose();
  }

  /**
   * Register a handler for confirmation requests
   * Returns unsubscribe function for cleanup
   */
  onConfirmationRequired(
    handler: (request: ConfirmationRequest) => void
  ): () => void {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  /**
   * Notify all handlers about a new confirmation request
   */
  private notifyHandlers(request: ConfirmationRequest): void {
    this.handlers.forEach(handler => {
      try {
        handler(request);
      } catch (error) {
        console.error('[AIConfirmationService] Handler error:', error);
      }
    });
  }

  /**
   * Respond to a confirmation request
   * sl-canvas2-8.31: Returns full response including toolResult
   */
  async respondToConfirmation(
    id: string,
    response: ConfirmationResponse
  ): Promise<ConfirmationApiResponse> {
    const path = `/api/ai/confirmations/${id}/respond`;
    const res = await defaultWorkspaceApiClient.request({
      workspaceId: this.workspaceService.workspace.id,
      path,
      init: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(response),
      },
      category: 'foreground-action',
      key: `POST:${path}`,
      mode: 'foreground',
      priority: 'foreground',
    });

    if (!res.ok) {
      throw new Error(`Failed to respond to confirmation: ${await res.text()}`);
    }

    const data: ConfirmationApiResponse = await res.json();
    return data;
  }
}
